import { resourceValidationIds } from './resource-validation-ids';
import { IDENTITY_MATRIX, multiplyMatrix, type Matrix } from '../core/geometry';
import type { EditorDocument, GroupLayer, Layer, LayerBase, TextLayer, VectorLayer } from '../core/model';
import { EMPTY_SELECTION } from '../core/selection';
import type { SolidPaint, VectorObject, VectorStroke } from '../vector/types';
import type {
  MaipoPublicSceneV01,
  PublicFont,
  PublicNode,
  PublicSolidPaint,
  PublicStroke,
  PublicTransform,
  PublicVectorObject,
} from '@maipo/format';
import { parseMaipoPublicScene, sceneSupportDisposition } from '@maipo/format';

const PUBLIC_TRANSFORM_EXTENSION = 'maipo:public-transform';
const PUBLIC_ROLE_EXTENSION = 'maipo:public-role';
const PUBLIC_FONT_REF_EXTENSION = 'maipo:public-font-ref';
const PUBLIC_EXTENSIONS_EXTENSION = 'maipo:public-extensions';
const PUBLIC_DOCUMENT_EXTENSION = 'maipo:public-document';
const SYNTHETIC_ROOT_ID = '@maipo:public-root';
const SYNTHETIC_BACKGROUND_ID = '@maipo:canvas-background';
const SYNTHETIC_BACKGROUND_OBJECT_ID = '@maipo:canvas-background-fill';
const FIXED_TIMESTAMP = '1970-01-01T00:00:00.000Z';

export class UnsupportedPublicFeatureError extends Error {
  readonly feature: string;
  readonly target: string;

  constructor(feature: string, target: string) {
    super(`Maipo public feature ${feature} at ${target} is not supported by this adapter.`);
    this.name = 'UnsupportedPublicFeatureError';
    this.feature = feature;
    this.target = target;
  }
}

export class UnsupportedNativeFeatureError extends Error {
  readonly feature: string;
  readonly target: string;

  constructor(feature: string, target: string) {
    super(`Maipo native feature ${feature} at ${target} cannot be represented by public static-core 0.1.`);
    this.name = 'UnsupportedNativeFeatureError';
    this.feature = feature;
    this.target = target;
  }
}

function snap(value: number): number {
  if (Object.is(value, -0) || Math.abs(value) < 1e-12) return 0;
  const integer = Math.round(value);
  if (Math.abs(value - integer) < 1e-12) return integer;
  return value;
}

/** Convert the public scale → x-skew → rotation → translation contract to a matrix. */
export function publicTransformMatrix(transform: PublicTransform = {}): Matrix {
  const radians = ((transform.rotation ?? 0) * Math.PI) / 180;
  const skew = Math.tan(((transform.skewX ?? 0) * Math.PI) / 180);
  const scale: Matrix = [transform.scaleX ?? 1, 0, 0, transform.scaleY ?? 1, 0, 0];
  const skewX: Matrix = [1, 0, skew, 1, 0, 0];
  const rotate: Matrix = [Math.cos(radians), Math.sin(radians), -Math.sin(radians), Math.cos(radians), 0, 0];
  const translate: Matrix = [1, 0, 0, 1, transform.x ?? 0, transform.y ?? 0];
  return multiplyMatrix(translate, multiplyMatrix(rotate, multiplyMatrix(skewX, scale))).map(snap) as unknown as Matrix;
}

function approximately(left: number, right: number, epsilon = 1e-9): boolean {
  return Math.abs(left - right) <= epsilon * Math.max(1, Math.abs(left), Math.abs(right));
}

/** Decompose a native matrix into the public translate → rotate → x-skew → scale contract. */
export function matrixToPublicTransform(matrix: Matrix, target = 'transform'): PublicTransform {
  if (!matrix.every(Number.isFinite)) throw new UnsupportedNativeFeatureError('non-finite transform', target);
  const [a, b, c, d, x, y] = matrix;
  const columnOne = Math.hypot(a, b);
  const columnTwo = Math.hypot(c, d);
  const determinant = a * d - b * c;
  let rotation = 0;
  let scaleX = 0;
  let scaleY = 0;
  let skewX = 0;
  if (columnOne > 1e-12 && Math.abs(determinant) > 1e-12) {
    scaleX = columnOne;
    scaleY = determinant / scaleX;
    rotation = Math.atan2(b, a);
    skewX = Math.atan((a * c + b * d) / (scaleX * scaleY));
  } else if (columnOne > 1e-12 && columnTwo <= 1e-12) {
    scaleX = columnOne;
    rotation = Math.atan2(b, a);
  } else if (columnOne <= 1e-12 && columnTwo > 1e-12) {
    scaleY = columnTwo;
    rotation = Math.atan2(-c, d);
  } else if (columnOne > 1e-12 || columnTwo > 1e-12) {
    throw new UnsupportedNativeFeatureError('non-decomposable singular transform', target);
  }
  const result: PublicTransform = {
    x: snap(x),
    y: snap(y),
    rotation: snap(rotation * 180 / Math.PI),
    scaleX: snap(scaleX),
    scaleY: snap(scaleY),
    skewX: snap(skewX * 180 / Math.PI),
  };
  const reconstructed = publicTransformMatrix(result);
  if (!matrix.every((value, index) => approximately(value, reconstructed[index] ?? NaN))) {
    throw new UnsupportedNativeFeatureError('non-decomposable affine transform', target);
  }
  return result;
}

function extensions(source: Record<string, unknown> | undefined, role?: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (source && Object.keys(source).length) result[PUBLIC_EXTENSIONS_EXTENSION] = structuredClone(source);
  if (role !== undefined) result[PUBLIC_ROLE_EXTENSION] = role;
  return result;
}

function publicMetadata(source: Record<string, unknown>, target: string): { role?: string; fontRef?: string; extensions?: Record<string, unknown> } {
  const result = structuredClone(source);
  const role = result[PUBLIC_ROLE_EXTENSION];
  const fontRef = result[PUBLIC_FONT_REF_EXTENSION];
  const opaqueExtensions = result[PUBLIC_EXTENSIONS_EXTENSION];
  delete result[PUBLIC_TRANSFORM_EXTENSION];
  delete result[PUBLIC_ROLE_EXTENSION];
  delete result[PUBLIC_FONT_REF_EXTENSION];
  delete result[PUBLIC_EXTENSIONS_EXTENSION];
  const opaque = opaqueExtensions === undefined
    ? undefined
    : typeof opaqueExtensions === 'object' && opaqueExtensions !== null && !Array.isArray(opaqueExtensions)
      ? structuredClone(opaqueExtensions as Record<string, unknown>)
      : null;
  if (opaque === null) throw new UnsupportedNativeFeatureError('invalid opaque public extensions', target);
  const privateKey = Object.keys(result)[0];
  if (privateKey) throw new UnsupportedNativeFeatureError(`native extension ${privateKey}`, target);
  if (role !== undefined && typeof role !== 'string') throw new UnsupportedNativeFeatureError('non-string public role metadata', target);
  if (fontRef !== undefined && typeof fontRef !== 'string') throw new UnsupportedNativeFeatureError('non-string public font metadata', target);
  return {
    ...(role === undefined ? {} : { role }),
    ...(fontRef === undefined ? {} : { fontRef }),
    ...(opaque === undefined || Object.keys(opaque).length === 0 ? {} : { extensions: opaque }),
  };
}

function isIdentity(matrix: Matrix): boolean {
  return matrix.every((value, index) => approximately(value, IDENTITY_MATRIX[index] ?? NaN));
}

function assertPortableLayerBase(layer: LayerBase): void {
  if (layer.mask) throw new UnsupportedNativeFeatureError('layer mask', layer.id);
  if (layer.pixelsLocked !== layer.locked || layer.positionLocked !== layer.locked) {
    throw new UnsupportedNativeFeatureError('independent pixel/position locks', layer.id);
  }
}

function preservedTransform(layer: LayerBase): PublicTransform {
  const original = layer.extensions[PUBLIC_TRANSFORM_EXTENSION];
  if (original !== undefined) {
    if (!original || typeof original !== 'object' || Array.isArray(original)
      || Object.entries(original).some(([key, value]) => !['x', 'y', 'rotation', 'scaleX', 'scaleY', 'skewX'].includes(key) || typeof value !== 'number' || !Number.isFinite(value))) {
      throw new UnsupportedNativeFeatureError('invalid retained public transform', layer.id);
    }
    const transform = original as PublicTransform;
    if (publicTransformMatrix(transform).every((value, index) => value === layer.transform[index])) return structuredClone(transform);
  }
  return matrixToPublicTransform(layer.transform, layer.id);
}

function publicLayerBase(layer: LayerBase): Omit<PublicNode, 'type'> {
  assertPortableLayerBase(layer);
  const metadata = publicMetadata(layer.extensions, layer.id);
  return {
    id: layer.id,
    name: layer.name,
    ...(metadata.role === undefined ? {} : { role: metadata.role }),
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    locked: layer.locked,
    clipping: layer.clipping,
    transform: preservedTransform(layer),
    ...(metadata.extensions === undefined ? {} : { extensions: metadata.extensions }),
  } as Omit<PublicNode, 'type'>;
}

function publicPaint(source: SolidPaint, target: string): PublicSolidPaint {
  if (source.swatchId !== undefined || source.tint !== undefined) throw new UnsupportedNativeFeatureError('swatch-linked paint', target);
  return { type: 'solid', color: source.color, opacity: source.alpha };
}

function publicStroke(source: VectorStroke, target: string): PublicStroke {
  return {
    paint: publicPaint(source.paint, target),
    width: source.width,
    lineCap: source.lineCap,
    lineJoin: source.lineJoin,
    dash: [...source.dash],
  };
}

function publicVectorObject(source: VectorObject): PublicVectorObject {
  const metadata = publicMetadata(source.extensions, source.id);
  const common = {
    id: source.id,
    name: source.name,
    ...(metadata.role === undefined ? {} : { role: metadata.role }),
    visible: source.visible,
    opacity: source.opacity,
    transform: matrixToPublicTransform(source.transform, source.id),
    fill: source.fill ? publicPaint(source.fill, source.id) : null,
    stroke: source.stroke ? publicStroke(source.stroke, source.id) : null,
    ...(metadata.extensions === undefined ? {} : { extensions: metadata.extensions }),
  };
  if (source.type === 'rectangle') return { ...common, type: 'rectangle', geometry: structuredClone(source.geometry) };
  if (source.type === 'ellipse') return { ...common, type: 'ellipse', geometry: structuredClone(source.geometry) };
  if (source.geometry.widthStops?.length) throw new UnsupportedNativeFeatureError('variable-width path', source.id);
  return {
    ...common,
    type: 'path',
    geometry: {
      nodes: source.geometry.nodes.map((node) => structuredClone(node)),
      subpaths: source.geometry.subpaths.map((subpath) => ({ ...structuredClone(subpath), nodeIds: [...subpath.nodeIds] })),
      fillRule: source.geometry.fillRule,
    },
  };
}

function systemFontId(font: Pick<PublicFont, 'family' | 'weight'>, claimed: Set<string>): string {
  const family = font.family.normalize('NFKD').replace(/[^A-Za-z0-9._-]+/g, '.').replace(/^[^A-Za-z0-9]+|[._-]+$/g, '').slice(0, 80) || 'face';
  const base = `font.system.${family}.${String(font.weight)}.normal`;
  let candidate = base;
  for (let suffix = 2; claimed.has(candidate); suffix += 1) candidate = `${base}.${String(suffix)}`;
  claimed.add(candidate);
  return candidate;
}

function fontReference(
  family: string,
  weight: number,
  preferred: string | undefined,
  fonts: PublicFont[],
  claimed: Set<string>,
  target: string,
): string {
  const preferredFont = preferred ? fonts.find((font) => font.id === preferred) : undefined;
  if (preferred && !preferredFont) throw new UnsupportedNativeFeatureError(`unresolved public font reference ${preferred}`, target);
  if (preferredFont?.family === family && preferredFont.weight === weight) {
    if (preferredFont.style !== 'normal') throw new UnsupportedNativeFeatureError(`font style ${preferredFont.style}`, target);
    return preferredFont.id;
  }
  const existing = fonts.find((font) => font.family === family && font.weight === weight && font.style === 'normal');
  if (existing) return existing.id;
  const next: PublicFont = {
    id: systemFontId({ family, weight }, claimed),
    family,
    weight,
    style: 'normal',
    source: { kind: 'system' },
    license: {
      spdx: 'LicenseRef-SystemFont',
      embedding: 'unknown',
      notice: 'Created from a native text edit; rendering depends on the local system font.',
    },
  };
  fonts.push(next);
  return next.id;
}

function layerBase(node: PublicNode, retainTransform = false): LayerBase {
  return {
    id: node.id,
    name: node.name,
    visible: node.visible ?? true,
    opacity: node.opacity ?? 1,
    fillOpacity: node.fillOpacity ?? 1,
    blendMode: node.blendMode ?? 'normal',
    locked: node.locked ?? false,
    pixelsLocked: node.locked ?? false,
    positionLocked: node.locked ?? false,
    color: 'none',
    clipping: node.clipping ?? false,
    transform: publicTransformMatrix(node.transform),
    extensions: { ...extensions(node.extensions, node.role), ...(retainTransform && node.transform ? { [PUBLIC_TRANSFORM_EXTENSION]: structuredClone(node.transform) } : {}) },
  };
}

function paint(source: PublicSolidPaint): SolidPaint {
  return { type: 'solid', color: source.color, alpha: source.opacity ?? 1 };
}

function stroke(source: PublicStroke): VectorStroke {
  return {
    paint: paint(source.paint),
    width: source.width,
    lineCap: source.lineCap,
    lineJoin: source.lineJoin,
    dash: [...source.dash],
  };
}

function vectorObject(source: PublicVectorObject): VectorObject {
  const common = {
    id: source.id,
    name: source.name,
    visible: source.visible ?? true,
    opacity: source.opacity ?? 1,
    transform: publicTransformMatrix(source.transform),
    fill: source.fill ? paint(source.fill) : null,
    stroke: source.stroke ? stroke(source.stroke) : null,
    extensions: extensions(source.extensions, source.role),
  };
  if (source.type === 'rectangle') return { ...common, type: 'rectangle', geometry: structuredClone(source.geometry) };
  if (source.type === 'ellipse') return { ...common, type: 'ellipse', geometry: structuredClone(source.geometry) };
  return { ...common, type: 'path', geometry: structuredClone(source.geometry) };
}

function publicNodeToLayer(node: PublicNode, draftImages = false): Layer {
  const base = layerBase(node, draftImages);
  if (node.type === 'group') {
    const group: GroupLayer = { ...base, kind: 'group', children: node.children.map(child => publicNodeToLayer(child, draftImages)), expanded: true };
    return group;
  }
  if (node.type === 'text') {
    const textExtensions = base.extensions;
    if (node.style.fontRef !== undefined) textExtensions[PUBLIC_FONT_REF_EXTENSION] = node.style.fontRef;
    const layer: TextLayer = {
      ...base,
      kind: 'text',
      text: {
        schemaVersion: 1,
        value: node.text,
        box: { width: node.frame.width, height: node.frame.height, autoHeight: node.frame.autoHeight ?? false },
        style: {
          family: node.style.fontFamily,
          weight: node.style.fontWeight,
          size: node.style.fontSize,
          lineHeight: node.style.lineHeight,
          tracking: node.style.letterSpacing,
          align: node.style.textAlign,
          color: node.style.color,
        },
        missingFont: false,
        extensions: {},
      },
    };
    return layer;
  }
  if (node.type === 'vector') {
    const layer: VectorLayer = {
      ...base,
      kind: 'vector',
      vector: {
        schemaVersion: 2,
        // Public arrays are top-to-bottom; the internal vector runtime paints
        // forward, so its storage order is bottom-to-top.
        objects: [...node.objects].reverse().map(vectorObject),
        extensions: {},
      },
    };
    return layer;
  }
  if (draftImages && node.type === 'image') return { ...base, kind: 'image', image: { asset: node.asset, frame: structuredClone(node.frame) } };
  throw new UnsupportedPublicFeatureError('image', node.id);
}

function assertSupportedPublicFonts(scene: MaipoPublicSceneV01): void {
  const fonts = new Map(scene.fonts.map((font) => [font.id, font]));
  const visit = (node: PublicNode): void => {
    if (node.type === 'group') node.children.forEach(visit);
    else if (node.type === 'text' && node.style.fontRef) {
      const font = fonts.get(node.style.fontRef);
      if (font && font.style !== 'normal') throw new UnsupportedPublicFeatureError(`font style ${font.style}`, node.id);
    }
  };
  scene.children.forEach(visit);
}

function backgroundLayer(scene: MaipoPublicSceneV01): VectorLayer | null {
  if (scene.canvas.background.type === 'transparent') return null;
  return {
    id: SYNTHETIC_BACKGROUND_ID,
    name: 'Canvas background',
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: 'normal',
    locked: true,
    pixelsLocked: true,
    positionLocked: true,
    color: 'none',
    clipping: false,
    transform: IDENTITY_MATRIX,
    extensions: {},
    kind: 'vector',
    vector: {
      schemaVersion: 2,
      extensions: {},
      objects: [{
        id: SYNTHETIC_BACKGROUND_OBJECT_ID,
        name: 'Canvas background fill',
        visible: true,
        opacity: 1,
        transform: IDENTITY_MATRIX,
        fill: { type: 'solid', color: scene.canvas.background.color, alpha: 1 },
        stroke: null,
        extensions: {},
        type: 'rectangle',
        geometry: {
          x: 0,
          y: 0,
          width: scene.canvas.width,
          height: scene.canvas.height,
          radius: { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 },
        },
      }],
    },
  };
}

export function publicSceneToEditorDocument(value: unknown, options: { draftImages?: boolean } = {}): EditorDocument {
  const scene = parseMaipoPublicScene(value);
  const unsupported = sceneSupportDisposition(scene).unsupported[0];
  if (unsupported) throw new UnsupportedPublicFeatureError(`required extension ${unsupported.key}@${unsupported.version}`, unsupported.key);
  assertSupportedPublicFonts(scene);
  const children = scene.children.map(node => publicNodeToLayer(node, options.draftImages));
  const background = backgroundLayer(scene);
  if (background) children.push(background);
  const root: GroupLayer = {
    id: SYNTHETIC_ROOT_ID,
    name: 'Root',
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: 'normal',
    locked: false,
    pixelsLocked: false,
    positionLocked: false,
    color: 'none',
    clipping: false,
    transform: IDENTITY_MATRIX,
    extensions: {},
    kind: 'group',
    children,
    expanded: true,
  };
  return {
    schemaVersion: 1,
    id: scene.documentId,
    name: scene.name,
    width: scene.canvas.width,
    height: scene.canvas.height,
    resolution: { x: 72, y: 72, unit: 'ppi' },
    color: { space: 'srgb', depth: 8 },
    pixelAspectRatio: 1,
    root,
    selection: structuredClone(EMPTY_SELECTION),
    activeLayerIds: scene.children[0] ? [scene.children[0].id] : [],
    createdAt: FIXED_TIMESTAMP,
    updatedAt: FIXED_TIMESTAMP,
    source: { kind: 'maipo', name: scene.name },
    extensions: {
      ...(scene.extensions && Object.keys(scene.extensions).length ? { [PUBLIC_EXTENSIONS_EXTENSION]: structuredClone(scene.extensions) } : {}),
      [PUBLIC_DOCUMENT_EXTENSION]: {
        format: scene.format,
        version: scene.version,
        revision: scene.revision,
        extensionDeclarations: structuredClone(scene.extensionDeclarations ?? []),
        fonts: structuredClone(scene.fonts),
        assets: structuredClone(scene.assets),
      },
    },
  };
}

function assertPortableRoot(root: GroupLayer): void {
  if (!root.visible || root.opacity !== 1 || root.fillOpacity !== 1 || root.blendMode !== 'normal' || root.locked || root.pixelsLocked || root.positionLocked || root.clipping || root.mask || !isIdentity(root.transform)) {
    throw new UnsupportedNativeFeatureError('root appearance or transform', root.id);
  }
  const metadata = publicMetadata(root.extensions, root.id);
  if (metadata.role !== undefined || metadata.fontRef !== undefined || metadata.extensions !== undefined) {
    throw new UnsupportedNativeFeatureError('root metadata', root.id);
  }
}

function canvasBackground(layer: Layer, document: EditorDocument): MaipoPublicSceneV01['canvas']['background'] {
  if (layer.id !== SYNTHETIC_BACKGROUND_ID || layer.kind !== 'vector') throw new UnsupportedNativeFeatureError('invalid canvas background layer', layer.id);
  if (!layer.visible || layer.opacity !== 1 || layer.fillOpacity !== 1 || layer.blendMode !== 'normal' || !layer.locked || !layer.pixelsLocked || !layer.positionLocked || layer.clipping || layer.mask || !isIdentity(layer.transform)) {
    throw new UnsupportedNativeFeatureError('modified canvas background layer', layer.id);
  }
  if (Object.keys(layer.extensions).length || Object.keys(layer.vector.extensions).length || layer.vector.schemaVersion !== 2 || layer.vector.objects.length !== 1) {
    throw new UnsupportedNativeFeatureError('modified canvas background structure', layer.id);
  }
  const object = layer.vector.objects[0];
  if (!object || object.id !== SYNTHETIC_BACKGROUND_OBJECT_ID || object.type !== 'rectangle' || !object.visible || object.opacity !== 1 || !isIdentity(object.transform) || object.stroke || !object.fill || object.fill.type !== 'solid' || object.fill.alpha !== 1 || object.fill.swatchId !== undefined || object.fill.tint !== undefined || Object.keys(object.extensions).length) {
    throw new UnsupportedNativeFeatureError('modified canvas background object', layer.id);
  }
  const radius = object.geometry.radius;
  if (object.geometry.x !== 0 || object.geometry.y !== 0 || object.geometry.width !== document.width || object.geometry.height !== document.height || radius.topLeft !== 0 || radius.topRight !== 0 || radius.bottomRight !== 0 || radius.bottomLeft !== 0) {
    throw new UnsupportedNativeFeatureError('modified canvas background geometry', layer.id);
  }
  return { type: 'solid', color: object.fill.color };
}

function documentPublicMetadata(document: EditorDocument): { revision: number; extensionDeclarations: MaipoPublicSceneV01['extensionDeclarations'] } {
  const metadata = document.extensions[PUBLIC_DOCUMENT_EXTENSION];
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return { revision: 0, extensionDeclarations: [] };
  const revision = (metadata as Record<string, unknown>).revision;
  const declarations = (metadata as Record<string, unknown>).extensionDeclarations;
  return {
    revision: typeof revision === 'number' && Number.isInteger(revision) && revision >= 0 ? revision : 0,
    extensionDeclarations: Array.isArray(declarations) ? structuredClone(declarations) as MaipoPublicSceneV01['extensionDeclarations'] : [],
  };
}

/** Resource tables travel with the native document, including through native ZIP saves. */
export function documentPublicResources(document: EditorDocument): Pick<MaipoPublicSceneV01, 'fonts' | 'assets'> {
  const metadata = document.extensions[PUBLIC_DOCUMENT_EXTENSION];
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return { fonts: [], assets: [] };
  const stored = metadata as Record<string, unknown>;
  // Older projections did not retain tables. An explicit base scene can supply
  // them; unresolved bindings otherwise fail instead of changing to system fonts.
  const validationId = resourceValidationIds(stored.fonts, stored.assets);
  const resources = parseMaipoPublicScene({
    format: 'maipo-scene', version: '0.1', documentId: validationId(), revision: 0,
    name: 'Native resources',
    canvas: { width: 1, height: 1, colorSpace: 'srgb', background: { type: 'transparent' } },
    fonts: stored.fonts === undefined ? [] : stored.fonts,
    assets: stored.assets === undefined ? [] : stored.assets,
    children: [],
  });
  return { fonts: resources.fonts, assets: resources.assets };
}

export function stampPublicDocumentMetadata(document: EditorDocument, scene: MaipoPublicSceneV01): void {
  document.extensions[PUBLIC_DOCUMENT_EXTENSION] = {
    format: scene.format,
    version: scene.version,
    revision: scene.revision,
    extensionDeclarations: structuredClone(scene.extensionDeclarations ?? []),
    fonts: structuredClone(scene.fonts),
    assets: structuredClone(scene.assets),
  };
}

export interface PublicSceneExportOptions {
  /** Unpublished image bridge; never enables a published renderer profile. */
  draftImages?: boolean;
  baseScene?: unknown;
  revision?: number;
}

/** Export the native static-core projection without rasterization or feature loss. */
export function editorDocumentToPublicScene(document: EditorDocument, options: PublicSceneExportOptions = {}): MaipoPublicSceneV01 {
  if (document.schemaVersion !== 1 || document.color.space !== 'srgb' || document.color.depth !== 8 || document.pixelAspectRatio !== 1) {
    throw new UnsupportedNativeFeatureError('document color/version metadata', document.id);
  }
  assertPortableRoot(document.root);
  const base = options.baseScene === undefined ? undefined : parseMaipoPublicScene(options.baseScene);
  if (base && base.documentId !== document.id) throw new Error(`Public base scene ${base.documentId} does not match native document ${document.id}.`);
  const resources = base ?? documentPublicResources(document);
  const fonts = structuredClone(resources.fonts);
  const assets = structuredClone(resources.assets);
  const claimed = new Set<string>([document.id, ...fonts.map((font) => font.id), ...assets.map((asset) => asset.id)]);
  const claimNativeIds = (layer: Layer): void => {
    claimed.add(layer.id);
    if (layer.kind === 'group') layer.children.forEach(claimNativeIds);
    else if (layer.kind === 'vector') for (const object of layer.vector.objects) {
      claimed.add(object.id);
      if (object.type === 'path') {
        object.geometry.nodes.forEach((node) => claimed.add(node.id));
        object.geometry.subpaths.forEach((subpath) => claimed.add(subpath.id));
      }
    }
  };
  document.root.children.forEach(claimNativeIds);
  const layers = [...document.root.children];
  const backgroundIndex = layers.findIndex((layer) => layer.id === SYNTHETIC_BACKGROUND_ID);
  if (backgroundIndex >= 0 && backgroundIndex !== layers.length - 1) {
    throw new UnsupportedNativeFeatureError('canvas background stack order', SYNTHETIC_BACKGROUND_ID);
  }
  const background = backgroundIndex < 0 ? { type: 'transparent' as const } : canvasBackground(layers.pop()!, document);

  const toNode = (layer: Layer): PublicNode => {
    const common = publicLayerBase(layer);
    if (layer.kind === 'group') return { ...common, type: 'group', children: layer.children.map(toNode) } as PublicNode;
    if (layer.kind === 'text') {
      if (layer.text.schemaVersion !== 1 || Object.keys(layer.text.extensions).length) throw new UnsupportedNativeFeatureError('text extension or version', layer.id);
      const metadata = publicMetadata(layer.extensions, layer.id);
      const fontRef = fontReference(layer.text.style.family, layer.text.style.weight, metadata.fontRef, fonts, claimed, layer.id);
      return {
        ...common,
        type: 'text',
        text: layer.text.value,
        frame: { width: layer.text.box.width, height: layer.text.box.height, autoHeight: layer.text.box.autoHeight },
        style: {
          fontRef,
          fontFamily: layer.text.style.family,
          fontWeight: layer.text.style.weight,
          fontSize: layer.text.style.size,
          lineHeight: layer.text.style.lineHeight,
          letterSpacing: layer.text.style.tracking,
          textAlign: layer.text.style.align,
          color: layer.text.style.color,
        },
      } as PublicNode;
    }
    if (layer.kind === 'vector') {
      if (layer.vector.schemaVersion !== 2 || Object.keys(layer.vector.extensions).length) throw new UnsupportedNativeFeatureError('vector scene extension or version', layer.id);
      return { ...common, type: 'vector', objects: [...layer.vector.objects].reverse().map(publicVectorObject) } as PublicNode;
    }
    if (layer.kind === 'image' && options.draftImages) {
      const asset = assets.find(item => item.id === layer.image.asset);
      if (!asset || !asset.mediaType.startsWith('image/')) throw new UnsupportedNativeFeatureError('unresolved image asset', layer.id);
      return { ...common, type: 'image', asset: layer.image.asset, frame: structuredClone(layer.image.frame) } as PublicNode;
    }
    throw new UnsupportedNativeFeatureError(`${layer.kind} layer`, layer.id);
  };

  const documentMetadata = structuredClone(document.extensions);
  delete documentMetadata[PUBLIC_DOCUMENT_EXTENSION];
  const publicDocumentExtensions = publicMetadata(documentMetadata, document.id);
  if (publicDocumentExtensions.role !== undefined || publicDocumentExtensions.fontRef !== undefined) throw new UnsupportedNativeFeatureError('invalid public document metadata', document.id);
  const storedMetadata = documentPublicMetadata(document);
  return parseMaipoPublicScene({
    format: 'maipo-scene',
    version: '0.1',
    documentId: document.id,
    revision: options.revision ?? base?.revision ?? storedMetadata.revision,
    name: document.name,
    canvas: { width: document.width, height: document.height, colorSpace: 'srgb', background },
    fonts,
    assets,
    children: layers.map(toNode),
    ...((base?.extensionDeclarations ?? storedMetadata.extensionDeclarations)?.length ? { extensionDeclarations: structuredClone(base?.extensionDeclarations ?? storedMetadata.extensionDeclarations ?? []) } : {}),
    ...(publicDocumentExtensions.extensions === undefined ? {} : { extensions: publicDocumentExtensions.extensions }),
  });
}
